import "core-js/modules/web.dom-collections.iterator.js";
var __makeTemplateObject = this && this.__makeTemplateObject || function (cooked, raw) {
  if (Object.defineProperty) {
    Object.defineProperty(cooked, "raw", {
      value: raw
    });
  } else {
    cooked.raw = raw;
  }
  return cooked;
};
var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { lazy, Suspense, useEffect } from 'react';
import { ApolloClient, ApolloLink, ApolloProvider, createHttpLink, from, InMemoryCache } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { AriaLiveProvider, Loader, ThemeProvider, themes } from '@digitools/honest-react';
import LogRocket from 'logrocket';
import { BrowserRouter as Router, matchPath, Redirect, Route, Switch } from 'react-router-dom';
import styled from 'styled-components';
import { Footer, HeaderContainer } from '../components/index';
import NeedHelp from '../components/NeedHelp';
import RegistrationOverlay from '../components/PageOverlays/RegistrationOverlay';
import PrivateRoute from '../components/Route/PrivateRoute';
import TimeoutModal from '../components/TimeoutModal/TimeoutModal';
import useAppLoader from '../hooks/useAppLoader';
import useAuthentication from '../hooks/useAuthentication';
import useFeatureToggle from '../hooks/useFeatureToggle';
import AppLoaderProvider from '../providers/AppLoaderProvider';
import AuthenticationProvider from '../providers/AuthenticationProvider';
import FeatureToggleProvider, { Features } from '../providers/FeatureToggleProvider';
import LanguageProvider from '../providers/LanguageProvider';
import MemberSearchProvider from '../providers/MemberSearchProvider';
import TealiumProvider from '../providers/TealiumProvider';
import { getCookie } from '../utils/cookieUtils';
import usePortalStatus from '../hooks/usePortalStatus';
import SystemDownOverlay from '../components/PageOverlays/SystemDownOverlay';
import PortalStatusProvider from '../providers/PortalStatusProvider';
import LogRocketProvider from '../components/LogRocket/LogRocketProvider';
// pages
var Login = /*#__PURE__*/lazy(function () {
  return import('../pages/Login/Login');
});
var CallbackV2 = /*#__PURE__*/lazy(function () {
  return import('../pages/Callback/CallbackV2');
});
var VerifyEmail = /*#__PURE__*/lazy(function () {
  return import('../pages/Login/VerifyEmail');
});
var Registration = /*#__PURE__*/lazy(function () {
  return import('../pages/Registration/Registration');
});
var PageNotFound = /*#__PURE__*/lazy(function () {
  return import('../pages/PageNotFound/PageNotFound');
});
var Profile = /*#__PURE__*/lazy(function () {
  return import('../pages/Profile/Profile');
});
var LandingPage = /*#__PURE__*/lazy(function () {
  return import('../pages/LandingPage/LandingPage');
});
var TermsAndConditionsPage = /*#__PURE__*/lazy(function () {
  return import('../pages/TermsAndConditions/TermsAndConditionsPage');
});
var AccessibilityPage = /*#__PURE__*/lazy(function () {
  return import('../pages/Accessibility/AccessibilityPage');
});
var BenefitDetails = /*#__PURE__*/lazy(function () {
  return import('../pages/BenefitDetails/BenefitsDetails');
});
var NetworkSearch = /*#__PURE__*/lazy(function () {
  return import('../pages/NetworkSearch/NetworkSearchPage');
});
var Claims = /*#__PURE__*/lazy(function () {
  return import('../pages/Claims/Claims');
});
var HelpCenter = /*#__PURE__*/lazy(function () {
  return import('../pages/HelpCenter/HelpCenter');
});
var MainContainer = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  height: 100vh;\n"], ["\n  display: flex;\n  flex-direction: column;\n  height: 100vh;\n"])));
var PageContainer = styled.div(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  flex: 1 0 auto;\n  margin-bottom: 2rem;\n"], ["\n  flex: 1 0 auto;\n  margin-bottom: 2rem;\n"])));
var StyledLoader = styled(Loader)(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  position: fixed;\n"], ["\n  position: fixed;\n"])));
// Needed for logrocket to capture graphql calls
// https://docs.logrocket.com/docs/graphql
var fetcher = function () {
  var args = [];
  for (var _i = 0; _i < arguments.length; _i++) {
    args[_i] = arguments[_i];
  }
  //@ts-ignore
  return window.fetch.apply(window, args);
};
var httpLink = createHttpLink({
  uri: '/dental/bff/graphql',
  fetch: fetcher
});
var authMiddleware = new ApolloLink(function (operation, forward) {
  var recordingURL;
  LogRocket.getSessionURL(function (sessionURL) {
    recordingURL = sessionURL;
  });
  // add the authorization to the headers
  operation.setContext(function (_a) {
    var _b = _a.headers,
      headers = _b === void 0 ? {} : _b;
    return {
      headers: __assign(__assign({}, headers), {
        'Accept-Encoding': 'gzip',
        authorization: "Bearer ".concat(getCookie('dppAuthToken')),
        'X-LogRocket-URL': recordingURL
      })
    };
  });
  return forward(operation);
});
var errorLink = onError(function (_a) {
  var response = _a.response,
    operation = _a.operation;
  var traceId = operation.getContext().response.headers.get('dpp-trace-id');
  if (response && response.errors) {
    response.errors.forEach(function (error) {
      if (error.extensions) {
        error.extensions['traceId'] = traceId;
      }
    });
  }
});
var apolloGraphQlClient = new ApolloClient({
  link: from([authMiddleware, errorLink, httpLink]),
  cache: new InMemoryCache()
});
var MainContent = function () {
  var _a = useAuthentication(),
    renewSession = _a.renewSession,
    loading = _a.loading,
    isAuthenticated = _a.isAuthenticated,
    handleAdfsConnection = _a.handleAdfsConnection,
    user = _a.user;
  var appLoading = useAppLoader().appLoading;
  var featureEnabled = useFeatureToggle().featureEnabled;
  var _b = usePortalStatus(),
    maintenanceModeEnabled = _b.maintenanceModeEnabled,
    maintenanceModeBackdoorAccessEnabled = _b.maintenanceModeBackdoorAccessEnabled;
  useEffect(function () {
    if (!loading && isAuthenticated) {
      renewSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);
  var Adfs = function () {
    handleAdfsConnection();
    return _jsx(_Fragment, {});
  };
  return _jsx(ApolloProvider, __assign({
    client: apolloGraphQlClient
  }, {
    children: _jsx(StyledLoader, __assign({
      viewportCentered: true,
      isShown: appLoading
    }, {
      children: _jsxs(MainContainer, {
        children: [(maintenanceModeEnabled || maintenanceModeBackdoorAccessEnabled && !(user === null || user === void 0 ? void 0 : user.internalAdmin)) && _jsx(SystemDownOverlay, {}), _jsx(RegistrationOverlay, {}), _jsx(HeaderContainer, {}), _jsx(AriaLiveProvider, {
          children: _jsx(PageContainer, {
            children: _jsxs(Suspense, __assign({
              fallback: 'Loading...'
            }, {
              children: [_jsxs(Switch, {
                children: [_jsx(Route, __assign({
                  exact: true,
                  path: '/'
                }, {
                  children: _jsx(Redirect, {
                    to: '/login'
                  })
                })), _jsx(Route, {
                  path: '/login/verify-email',
                  component: VerifyEmail
                }), _jsx(Route, {
                  path: '/login',
                  component: Login
                }), _jsx(Route, {
                  path: '/registration',
                  component: Registration
                }), _jsx(Route, {
                  path: '/v2/callback',
                  component: CallbackV2
                }), _jsx(Route, {
                  path: '/adfs',
                  component: Adfs
                }), _jsx(Route, {
                  path: '/terms-and-conditions',
                  component: TermsAndConditionsPage
                }), _jsx(Route, {
                  path: '/accessibility',
                  component: AccessibilityPage
                }), _jsx(PrivateRoute, {
                  path: '/home',
                  component: LandingPage
                }), _jsx(PrivateRoute, {
                  path: '/benefit-details',
                  component: BenefitDetails
                }), _jsx(PrivateRoute, {
                  path: '/network-search',
                  component: NetworkSearch
                }), featureEnabled(Features.PROFILE) && _jsx(PrivateRoute, {
                  path: '/profile',
                  component: Profile
                }), _jsx(PrivateRoute, {
                  path: '/claims',
                  component: Claims
                }), _jsx(PrivateRoute, {
                  path: '/help-center',
                  component: HelpCenter
                }), _jsx(Route, {
                  path: '*',
                  component: PageNotFound
                })]
              }), _jsx(TimeoutModal, {
                seconds: 1200,
                sRemainingForModal: 60
              })]
            }))
          })
        }), matchPath(window.location.pathname, {
          path: '/dental/registration',
          strict: true
        }) && _jsx(NeedHelp, {}), _jsx(Footer, {})]
      })
    }))
  }));
};
var App = function () {
  return _jsx(Router, __assign({
    basename: '/dental'
  }, {
    children: _jsx(AuthenticationProvider, {
      children: _jsx(LogRocketProvider, {
        children: _jsx(FeatureToggleProvider, {
          children: _jsx(TealiumProvider, {
            children: _jsx(ThemeProvider, __assign({
              theme: themes.lincoln
            }, {
              children: _jsx(LanguageProvider, {
                children: _jsx(MemberSearchProvider, {
                  children: _jsx(AppLoaderProvider, {
                    children: _jsx(PortalStatusProvider, {
                      children: _jsx(MainContent, {})
                    })
                  })
                })
              })
            }))
          })
        })
      })
    })
  }));
};
export { MainContent };
export default App;
var templateObject_1, templateObject_2, templateObject_3;